import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../account";
import { sendClientTelemetry } from "./clientTelemetry";
import { getEnvironmentFingerprint } from "./collectBrowserTelemetry";

const ENVIRONMENT_KEY = "mesfClientTelemetryEnvironment";
const LAST_USER_KEY = "mesfClientTelemetryLastUser";
const LAST_SEEN_KEY = "mesfClientTelemetryLastSeen";
const LONG_INACTIVITY_MS = 8 * 60 * 60 * 1000;

const getCurrentUserId = () => {
	try {
		const userData = JSON.parse(localStorage.getItem("userMESData") || "{}");
		return userData?.id ? String(userData.id) : "";
	} catch {
		return "";
	}
};

export const useClientTelemetry = (enabled = true) => {
	const { token } = useContext(AuthContext);
	const location = useLocation();
	const previousPathRef = useRef<string>();
	const sessionStartedRef = useRef(false);

	useEffect(() => {
		if (!enabled || !token || sessionStartedRef.current) {
			return;
		}

		sessionStartedRef.current = true;
		sendClientTelemetry({ eventType: "session_start" });

		const currentFingerprint = getEnvironmentFingerprint();
		const previousFingerprint = localStorage.getItem(ENVIRONMENT_KEY);
		const currentUserId = getCurrentUserId();
		const previousUserId = localStorage.getItem(LAST_USER_KEY);
		const previousSeenAt = Number(localStorage.getItem(LAST_SEEN_KEY) || 0);
		const inactiveForMs = previousSeenAt ? Date.now() - previousSeenAt : 0;

		if (
			previousFingerprint !== currentFingerprint ||
			(previousUserId && previousUserId !== currentUserId) ||
			inactiveForMs > LONG_INACTIVITY_MS
		) {
			sendClientTelemetry({
				eventType: "environment_change",
				payload: {
					environmentChanged: previousFingerprint !== currentFingerprint,
					userChanged: Boolean(
						previousUserId && previousUserId !== currentUserId,
					),
					inactiveForMs,
				},
			});
		}

		localStorage.setItem(ENVIRONMENT_KEY, currentFingerprint);
		localStorage.setItem(LAST_USER_KEY, currentUserId);
		localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
	}, [enabled, token]);

	useEffect(() => {
		if (!enabled || !token) {
			return;
		}

		const timer = window.setInterval(
			() => {
				localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
				sendClientTelemetry({ eventType: "heartbeat" });
			},
			10 * 60 * 1000,
		);

		return () => window.clearInterval(timer);
	}, [enabled, token]);

	useEffect(() => {
		if (!enabled || !token) {
			return;
		}

		const path = location.pathname + location.search;
		const previousPath = previousPathRef.current;
		previousPathRef.current = path;

		sendClientTelemetry({
			eventType: "route_view",
			path,
			previousPath,
		});
	}, [enabled, token, location.pathname, location.search]);

	useEffect(() => {
		if (!enabled || !token) {
			return;
		}

		const onVisibilityChange = () => {
			const eventType =
				document.visibilityState === "visible"
					? "visibility_visible"
					: "visibility_hidden";

			sendClientTelemetry({
				eventType,
				keepalive: document.visibilityState !== "visible",
			});
		};

		document.addEventListener("visibilitychange", onVisibilityChange);
		return () =>
			document.removeEventListener("visibilitychange", onVisibilityChange);
	}, [enabled, token]);
};
